import React from "react";

interface MetricOption {
  id: string;
  name: string;
}

interface FieldSelectionModalProps {
  open: boolean;
  onClose: () => void;
  themeBox: boolean;
  // pass metric id + name from caller
  fieldOptions: MetricOption[];
  // selectedFields now contains metric ids
  selectedFields: string[];
  // toggle by metric id
  toggleField: (id: string) => void;
  maxFields: number;
  modalTargetBlockId: string | null;
  // formatBlocks.fields must contain metric ids
  formatBlocks: { id: string; name: string; fields: string[] }[];
  applyFields: () => void;
  // Already-added metric IDs (for GROUP workouts where fields aren't on the block itself)
  existingFieldIds?: string[];
}

const FieldSelectionModal: React.FC<FieldSelectionModalProps> = ({
  open,
  onClose,
  themeBox,
  fieldOptions,
  selectedFields,
  toggleField,
  maxFields,
  modalTargetBlockId,
  formatBlocks,
  applyFields,
  existingFieldIds = [],
}) => {
  if (!open) return null;
  const target = modalTargetBlockId
    ? formatBlocks.find((b) => b.id === modalTargetBlockId)
    : undefined;
  const initialRemaining = Math.max(
    0,
    maxFields - (target ? target.fields.length : 0)
  );
  const liveRemaining = Math.max(0, initialRemaining - selectedFields.length);
  const showScroll = fieldOptions.length > 5;


  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${themeBox ? "bg-black/70" : "bg-black/40"
        }`}
    >
      <div
        className={`relative w-full max-w-md rounded-xl shadow-xl border ${themeBox
          ? "bg-[#1E1E1E] border-[#2C2C2C] text-gray-100"
          : "bg-white border-gray-200 text-gray-900"
          }`}
      >
        {/* Scoped scrollbar styles — dark & light themed */}
        <style>{`
          .fsm-scroll-dark::-webkit-scrollbar { width: 5px; }
          .fsm-scroll-dark::-webkit-scrollbar-track { background: #111111; border-radius: 10px; }
          .fsm-scroll-dark::-webkit-scrollbar-thumb { background: #2A2A2A; border-radius: 10px; }
          .fsm-scroll-dark { scrollbar-width: thin; scrollbar-color: #2A2A2A #111111; }

          .fsm-scroll-light::-webkit-scrollbar { width: 5px; }
          .fsm-scroll-light::-webkit-scrollbar-track { background: #F5F7FA; border-radius: 10px; }
          .fsm-scroll-light::-webkit-scrollbar-thumb { background: #C9CED6; border-radius: 10px; }
          .fsm-scroll-light { scrollbar-width: thin; scrollbar-color: #C9CED6 #F5F7FA; }
        `}</style>
        <button
          onClick={onClose}
          aria-label="Close"
          className={`absolute top-4 right-4 p-1.5 rounded-lg border transition-colors ${themeBox
            ? "border-[#333] bg-[#222] text-gray-400 hover:bg-[#333]"
            : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div className="px-6 py-8">
          <h3 className="text-base font-bold mb-1 text-center">
            {/* {modalTargetBlockId ? "Add Fields to Block" : "Select Exercise Fields"} */}
            Select Exercise Fields
          </h3>
          <p
            className={`text-[12px] mb-6 text-center ${themeBox ? "text-gray-400" : "text-gray-600"
              }`}
          >
            You can select up to{" "}
            <span className="text-[#B6CB24] font-bold">
              {maxFields} fields
            </span>{" "}
            {target && <span className="opacity-60">(Remaining: {liveRemaining})</span>}
          </p>
          <div
            className={`rounded-xl border ${themeBox
              ? "border-[#2C2C2C] bg-[#0E0E0E]"
              : "border-gray-200 bg-gray-50"
              } px-4 py-4 space-y-3 ${showScroll
                ? `overflow-y-auto ${themeBox ? "fsm-scroll-dark" : "fsm-scroll-light"}`
                : "overflow-hidden"
              }`}
            style={showScroll ? { maxHeight: "220px" } : undefined}
          >
            {fieldOptions.map((fld) => {
              const alreadyInBlock = (!!target && target.fields.includes(fld.id)) || existingFieldIds.includes(fld.id);
              const checked = selectedFields.includes(fld.id);
              const disabled =
                alreadyInBlock ||
                (!checked && selectedFields.length >= initialRemaining);
              return (
                <label
                  key={fld.id}
                  className={`flex items-center gap-3 text-xs cursor-pointer group ${disabled ? "opacity-40 cursor-not-allowed" : ""
                    }`}
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => !disabled && toggleField(fld.id)}
                  />
                  <div
                    className={`w-5 h-5 flex items-center justify-center rounded-md border-2 transition-all ${checked
                      ? "bg-[#B6CB24] border-[#B6CB24]"
                      : themeBox
                        ? "border-[#333] hover:border-[#444]"
                        : "border-gray-300 hover:border-gray-400"
                      }`}
                  >
                    {checked && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2.5 6.5L4.5 8.5L9.5 3.5"
                          stroke="black"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span
                    className={`font-medium transition-colors ${themeBox
                      ? (checked ? "text-[#B6CB24]" : "text-gray-300 group-hover:text-white")
                      : (checked ? "text-lime-700" : "text-gray-700")
                      }`}
                  >
                    {fld.name}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              type="button"
              onClick={applyFields}
              disabled={selectedFields.length === 0}
              className="px-10 py-2.5 rounded-xl text-xs font-bold text-[#FFFFFF] shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
              style={{
                background: "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
            >
              Apply
            </button>
            <button
              type="button"
              onClick={onClose}
              className={`px-10 py-2.5 rounded-xl text-xs font-bold border transition-all active:scale-95 ${themeBox
                ? "border-[#333] text-gray-300 hover:bg-[#222]"
                : "border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              style={themeBox ? {
                background: "radial-gradient(94.17% 100% at 52.48% 0%, rgba(46, 46, 46, 0.80) 0%, rgba(71, 71, 71, 0.80) 100%)",
              } : {}}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldSelectionModal;
